import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import {
  type LanternWorkspace,
  type PersonaRunOutcome,
  useAdminTenant,
  useLanternWorkspaces,
  useRunTenantPersona,
  useSetTenantAgent,
  useSetTenantLanternWorkspace,
  useTenantAgents,
} from '@diggens/shared-hooks';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface AgentsTabProps {
  tenantId: string;
}

interface LanternWorkspaceLinkProps {
  tenantId: string;
  linked: string | null;
}

/**
 * The Lantern workspace the tenant's onboarding operator reads, chosen from the ones the super admin can see as Lantern
 * staff, or pasted as an id. Remounted with each saved link, which resets the choice.
 */
function LanternWorkspaceLink({ tenantId, linked }: LanternWorkspaceLinkProps) {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<LanternWorkspace | null>(null);
  const directory = useLanternWorkspaces({ search });
  const linkedWorkspace = useLanternWorkspaces({ workspaceId: linked }, !!linked).data?.[0];
  const setWorkspace = useSetTenantLanternWorkspace(tenantId);
  // A search term is not an id: only a chosen workspace, or a pasted id, can be linked
  const workspaceId = chosen?.workspace_id ?? (GUID.test(input.trim()) ? input.trim() : '');

  // Ask Lantern once typing pauses, not on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 250);
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Lantern workspace
        </Typography>
        <Chip label={linked ? 'Linked' : 'Not linked'} size="small" color={linked ? 'success' : 'default'} variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        The workspace the onboarding operator reads. It joins the subagents below only while a workspace is linked, and only super admins who belong to this tenant can run it, from the Chief of Staff.
      </Typography>
      {linked && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {linkedWorkspace ? `Linked to ${linkedWorkspace.workspace_name} (${linkedWorkspace.org_name})` : `Linked to ${linked}`}
        </Typography>
      )}
      {setWorkspace.error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {setWorkspace.error.message}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'flex-start' }}>
        <Autocomplete<LanternWorkspace, false, false, true>
          freeSolo
          fullWidth
          size="small"
          options={directory.data ?? []}
          // Lantern already matched the search
          filterOptions={(options) => options}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.workspace_name)}
          isOptionEqualToValue={(option, value) => option.workspace_id === value.workspace_id}
          value={chosen}
          inputValue={input}
          loading={directory.isFetching}
          disabled={setWorkspace.isPending}
          onChange={(_, value) => setChosen(value && typeof value !== 'string' ? value : null)}
          onInputChange={(_, value, reason) => {
            setInput(value);
            if (reason === 'input' || reason === 'clear') setChosen(null);
          }}
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              <Box>
                <Typography variant="body2">{option.workspace_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.org_name} · {option.workspace_id}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Workspace"
              placeholder="Search by workspace or org name, or paste an id"
              helperText={directory.error ? `${directory.error.message}. Paste the workspace id instead.` : undefined}
            />
          )}
        />
        <Button variant="contained" disabled={!workspaceId || workspaceId.toLowerCase() === linked || setWorkspace.isPending} onClick={() => setWorkspace.mutate(workspaceId)}>
          Link
        </Button>
        {linked && (
          <Button color="error" disabled={setWorkspace.isPending} onClick={() => setWorkspace.mutate(null)}>
            Unlink
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

/** How a run now ended, in one line: its status, then the ops sweep's new findings or why it did not succeed. */
function runSummary(outcome: PersonaRunOutcome): string {
  const recorded = outcome.findings?.recorded;
  if (outcome.status === 'succeeded') return typeof recorded === 'number' ? `Ran: succeeded, ${recorded} new finding${recorded === 1 ? '' : 's'}` : 'Ran: succeeded';
  return `Ran: ${outcome.status}${outcome.error ? `, ${outcome.error}` : ''}`;
}

/** Which subagents this tenant's Manager can hand plan steps to. */
export function AgentsTab({ tenantId }: AgentsTabProps) {
  const { data: roster, isLoading, error } = useTenantAgents(tenantId);
  const { data: tenant } = useAdminTenant(tenantId);
  const setAgent = useSetTenantAgent(tenantId);
  const runPersona = useRunTenantPersona(tenantId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary">
        The Chief of Staff plans work for the subagents switched on here, and the tenant’s admins can give them jobs. A plan with a subagent that changes data waits for one of the tenant’s admins to
        approve it. Tenant admins also recruit and dismiss these in Settings, under Team; switching one here overrides their choice.
      </Typography>
      {tenant && <LanternWorkspaceLink key={tenant.lantern_workspace_id ?? ''} tenantId={tenantId} linked={tenant.lantern_workspace_id ?? null} />}
      {/* A roster that fails to load leaves the workspace link above usable */}
      {(error || !roster) && <Alert severity="error">{error instanceof Error ? error.message : 'Could not load the subagents'}</Alert>}
      {setAgent.error && <Alert severity="error">{setAgent.error.message}</Alert>}
      {roster?.map((entry) => (
        <Paper key={entry.agent} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {entry.displayName}
                </Typography>
                {entry.writes && <Chip label="Changes data" size="small" color="warning" variant="outlined" />}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {entry.description}
              </Typography>
              {entry.updatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Last changed {new Date(entry.updatedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
              <FormControlLabel
                label={entry.enabled ? 'On' : 'Off'}
                control={<Switch checked={entry.enabled} disabled={setAgent.isPending} onChange={(event) => setAgent.mutate({ agent: entry.agent, enabled: event.target.checked })} />}
              />
              {/* A scheduled persona's brief can be run now, as its next firing would; the request lasts as long as the run */}
              {entry.enabled && entry.definition?.schedule && entry.definition.capabilities.includes('run_on_schedule') && (
                <Button size="small" variant="outlined" disabled={runPersona.isPending} onClick={() => runPersona.mutate(entry.agent)}>
                  {runPersona.isPending && runPersona.variables === entry.agent ? 'Running…' : 'Run now'}
                </Button>
              )}
            </Stack>
          </Stack>
          {runPersona.variables === entry.agent && runPersona.data && !runPersona.isPending && (
            <Alert severity={runPersona.data.status === 'succeeded' ? 'success' : 'warning'} sx={{ mt: 1 }}>
              {runSummary(runPersona.data)}
            </Alert>
          )}
          {runPersona.variables === entry.agent && runPersona.error && !runPersona.isPending && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {runPersona.error.message}
            </Alert>
          )}
        </Paper>
      ))}
    </Stack>
  );
}
